import React, { useEffect, useState } from 'react';
import { Download, FileText, Plus, Loader2 } from 'lucide-react';
import { fetchProtectedBlob } from './protectedMedia';

const API = process.env.REACT_APP_BACKEND_URL;

const readableSize = bytes => (bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`);

/** A generated image or deck held in cloud storage, fetched as an in-memory blob. */
export const AiMedia = ({ media, onAddToPage }) => {
  const isImage = (media.content_type || '').startsWith('image/');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    let objectUrl;
    if (!isImage) return undefined;
    fetchProtectedBlob(media.url)
      .then(url => { objectUrl = url; if (live) setPreview(url); else URL.revokeObjectURL(url); })
      .catch(err => { if (live) setError(err.message); });
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [media.url, isImage]);

  const download = async () => {
    setBusy(true);
    try {
      const blob = await fetchProtectedBlob(media.url);
      const link = document.createElement('a');
      link.href = blob;
      link.download = media.filename || 'officium-ai-file';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blob), 4000);
    } catch {
      setError('That file could not be downloaded. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 space-y-1.5" data-testid="kb-ai-media">
      {isImage ? (
        error ? <p className="text-[11px] text-rose-600" data-testid="kb-ai-media-error">{error}</p>
          : preview ? <img src={preview} alt={media.filename} className="max-h-64 w-full rounded-lg object-contain bg-white" data-testid="kb-ai-media-image" />
            : <div className="flex h-24 items-center justify-center rounded-lg bg-white/60"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-2" data-testid="kb-ai-media-file">
          <FileText className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1 truncate text-[11px] text-slate-600">{media.filename}</span>
          <span className="shrink-0 text-[10px] text-slate-400">{readableSize(media.size)}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={download} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600" data-testid="kb-ai-media-download">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}Download
        </button>
        {isImage && onAddToPage && (
          <button type="button" data-testid="kb-ai-media-add"
            onClick={() => onAddToPage(`<img src="${API}${media.url}" alt="${media.filename}" class="kb-image" />`)}
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600">
            <Plus className="h-3 w-3" />Add to page
          </button>
        )}
      </div>
    </div>
  );
};
